<script>
  // d3 stuff 
  import { extent, max, zip } from 'd3-array';
  import { axisBottom, axisLeft } from 'd3-axis';
  import { scaleLinear, scaleBand } from 'd3-scale';
  import { line as lineShape, curveCardinal } from 'd3-shape';
  import { monthTickValues, monthFormat, dateParser } from '../utils/d3-utils';

  // Components
  import Axis from './Axis.svelte';
  import Footnote from './Footnote.svelte';
  import Sourceline from './Sourceline.svelte';
  import LineMarker from './LineMarker.svelte'
  import ChartToggle from './ChartToggle.svelte';
  import ChartHeadline from './ChartHeadline.svelte';
  import LineAnnotation from './LineAnnotation.svelte';

  // Other imports
  import { intcomma } from 'journalize';
  import { __ } from '../utils/i18n';
  import { stripHtml } from '../utils/strip';
  import { slugify } from '../utils/slugify';
  import { randomString } from '../utils/random';
  import { movingAverage } from '../utils/moving-average';

  // Data configuration
  export let title;
  export let data;
  export let attrs;
  export let name;
  export let footnote = null;
  export let sourceline = null;
  export let selectedAttrIndex = 0;
  export let showLine = true;
  export let locale = document.documentElement.lang;
  $: selectedAttr = attrs[selectedAttrIndex];

  // Dimensions
  export let width;
  export let height = 300;
  export let margin = { top: 20, right: 10, bottom: 35, left: 75 };

  // Reactive data
  //hack for Jan 3rd data dump
  $: selectedData = (name=="cases")
  ? data.map((d) => ({
    date: d.date,
    value: d[selectedAttr.value],
    class: d.date==dateParser("2022-01-03").toString()? "incomplete": "complete",
  }))
  : data.map((d) => ({
    date: d.date,
    value: d[selectedAttr.value],
    class: d.class || 'complete',
  }));

  $: averageLine = selectedAttr.lineFunc ? selectedAttr.lineFunc(selectedData) : zip(
    selectedData.map((d) => d.date),
    movingAverage(
      selectedData.map((d) => d.value),
      7
    )
  );


  $: averageLine = selectedAttr.lineFunc ? selectedAttr.lineFunc(selectedData) : zip(
    selectedData.map((d) => d.date),
    movingAverage(
      selectedData.map((d) => d.value),
      7
    )
  );

  // $: l = selectedData.length;
  // $: low = Math.round(l * 0.001);
  // $: high = l - low;
  // $: removeOutliers = selectedData.slice(0,high);

  const titleId = `${randomString()}`;
  $: title = stripHtml(selectedAttr.headline);

  $: lineColor = selectedAttr.color;

  // Scales
  $: xRange = selectedData.map((d) => d.date);
  $: xScale = scaleBand()
      .domain(xRange)
      .range([0, width - margin.left - margin.right])
      .padding(0);
  $: bandwidth = xScale.bandwidth();

  $: yScaleType = selectedAttr.yScaleType || scaleLinear;
  $: yMin = selectedAttr.yMin || 0;
  $: yMax = name== "cases"? max( selectedData.filter((d)=> d.class != "incomplete"), (d)=>d.value) :max(selectedData, (d) => d.value)
  $: yScale = yScaleType()
      .domain([yMin, yMax])
      .nice()
      .range([height - margin.top - margin.bottom, 0]);
  $: y0 = yScale(yMin);

  // Shapes
  $: drawLine = lineShape()
    .defined((d) => !isNaN(d[1]))
    .x((d) => xScale(d[0]) + bandwidth / 2)
    .y((d) => yScale(d[1]))
    .curve(curveCardinal);

  // Axes
  export let getXTicks = (a) => {
    const xExtent = extent(a.map((d) => d.date));
    return monthTickValues(...xExtent);
  };
  export let formatYTicks = intcomma;
  export let formatXTicks = monthFormat;

  // Line annotation
  export let labelTarget = null;
  let labelX;
  let labelY;
  $: {
    // If an [x, y] value pair hasn't been provided by the user,
    // the record 15% of the way into the dataset is selected
    if (labelTarget == null) {
        let availableData = averageLine.filter((d) => !isNaN(d[1]));
        let targetIndex = Math.floor(availableData.length * 0.15);
        labelTarget = availableData[targetIndex];
    }
    // The [x, y] value pair is used to set the label position.
    labelX = xScale(labelTarget[0]);
    labelY = Math.min(height-margin.bottom-50, yScale(labelTarget[1] * 1.125));
  }

  // Events
  const handleToggle = (e) => {
    selectedAttrIndex = attrs.findIndex((d, i) => i == e.target.getAttribute('data-index'));
  }
</script>

{#if attrs.length > 1}
<div class="button-grid button-group">
  <div class="button-grid-group">
    <ChartToggle
      slug={`${slugify(title)}-toggle`}
      buttonList={attrs}
      buttonGroupClass='button-group'
      bind:selectedIndex={selectedAttrIndex}
      on:click={handleToggle}
    />
  </div>
</div>
{/if}

<ChartHeadline text={selectedAttr.headline} />

<div class="inner-container" bind:clientWidth={width}>
  <svg {width} {height} role="graphics-document" aria-labelledby="{titleId}" class="line-timeseries">
    <title id="{titleId}">{title}</title>
    <g transform="translate({margin.left},{margin.top})">
      <g class="axes">
        <Axis
          axis={axisLeft}
          scale={yScale}
          className="axis--y"
          tickSize={-(width - margin.left - margin.right)}
          tickFormat={formatYTicks}
          ticks="5"
        />
        <Axis
          axis={axisBottom}
          scale={xScale}
          className="axis--x"
          yTranslate={height - margin.top - margin.bottom}
          tickFormat={formatXTicks}
          tickValues={getXTicks(selectedData)}
        />
      </g>
      <g class="bars">
        {#each selectedData as obj, idx}
          <rect
            data-index={idx}
            data-x={obj.date}
            data-y={obj.value}
            data-class={obj.class}
            x={xScale(obj.date)}
            width={bandwidth}
            y={yScale(obj.value)}
            height={y0 - yScale(obj.value)}
            fill="{obj.class == 'complete' ? selectedAttr.barColor : '#dddddd'}"
            opacity="0.6"
          />
        {/each}
      </g>
      {#if showLine}
        <g class="average-line">
          <path
            stroke={selectedAttr.lineColor}
            stroke-width="3"
            stroke-linejoin="round"
            stroke-linecap="round"
            fill="none"
            d={drawLine(averageLine)}
          />
        </g>
        <g class="annotations" transform="translate({labelX},{labelY})">
          <g class="chart-label chart-label-halo">
            <text text-anchor="middle">
            {#if locale == 'es'}
              <tspan x="0" y="0">Promedio de</tspan>
              <tspan x="0" y="12">7 días</tspan>
            {:else}
              <tspan x="0" y="0">7-day</tspan>
              <tspan x="0" y="12">average</tspan>
            {/if}
            </text>
          </g>
          <g class="chart-label">
            <text text-anchor="middle">
              {#if locale == 'es'}
                <tspan x="0" y="0">Promedio de</tspan>
                <tspan x="0" y="12">7 días</tspan>
              {:else}
                <tspan x="0" y="0">7-day</tspan>
                <tspan x="0" y="12">average</tspan>
              {/if}
            </text>
          </g>
        </g>
      {/if}
    </g>
  </svg>
</div>

{#if footnote}
  <Footnote text={footnote} />
{/if}

{#if sourceline}
  <Sourceline text={sourceline} />
{/if}

